<template>
  <div class="input-wrapper">
    <input
      class="cruise-input text-ellipsis"
      :class="inputClass"
      :id="safeId"
      :type="inputType"
      v-bind="attrs"
      autocomplete="off"
      placeholder=""
      :value="modelValue"
      @blur="handleBlur"
      @focus="handleFocus"
      @input="handleInput"
      :disabled="disabled"
      :title="modelValue"
    />
    <label
      v-if="!modelValue && attrs.placeholder"
      class="input-placeholder text-ellipsis"
      :for="safeId"
      :title="attrs.placeholder"
    >
      {{ attrs.placeholder }}<span v-if="required" class="required-star">*</span>
    </label>
  </div>
</template>

<script>
import { sharedComputedProps } from '@/mixins/formMixins';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import { isFullWidth } from '@/utils/textWidthConverter';

export default {
  name: 'CruiseInput',
  emits: ['update:modelValue', 'onBlur', 'onFocus', 'onInput'],
  mixins: [sharedComputedProps],
  props: {
    modelValue: [String, Number],
    required: Boolean,
    isError: Boolean,
    regexFilter: [Object, String],
    regexReplace: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isAlertShown: false, // alert 상태 관리
      isBlocked: false, // 이후 입력 차단 상태
      firstInput: '', // 첫 입력된 전각 문자
    };
  },
  computed: {
    inputType() {
      return this.$attrs.type || 'text';
    },
    inputClass() {
      return {
        error: this.isError,
        disabled: this.disabled,
      };
    },
  },
  methods: {
    handleBlur(e) {
      this.$emit('onBlur', e);
    },
    handleFocus(e) {
      this.$emit('onFocus', e);
    },
    handleInput(e) {
      const filteredValue = e.target.value.replace(this.regexFilter, this.regexReplace);
      let sanitizedValue = this.$sanitize(filteredValue);

      // Backspace 허용 또는 전각이 아닌 경우 차단 해제
      if (this.isBlocked && (e.inputType === 'deleteContentBackward' || !isFullWidth(sanitizedValue))) {
        this.isBlocked = false;
      }

      if (
        this.inputType === 'userId' ||
        this.inputType === 'password' ||
        this.inputType === 'emailAddress' ||
        this.inputType === 'emailDomain'
      ) {
        if (this.detectFullWidthChar(e, sanitizedValue)) {
          return;
        }
        e.target.value = decodeHtmlEntities(sanitizedValue).replaceAll(' ', '');
      } else {
        e.target.value = decodeHtmlEntities(sanitizedValue);
      }

      this.$emit('update:modelValue', e.target.value);
      this.$emit('onInput', e);
    },
    detectFullWidthChar(e, sanitizedValue) {
      if (isFullWidth(sanitizedValue)) {
        e.preventDefault(); // 입력 차단

        // 첫 입력된 전각 값 저장
        if (!this.isAlertShown) {
          this.firstInput = sanitizedValue;
          this.isAlertShown = true;
          this.isBlocked = true; // 이후 입력 차단
          this.$alert({ content: this.$t('messages.fullToHalfError') }).finally(() => {
            this.isAlertShown = false;
          });
        }

        // 첫 번째 전각 입력값만 반영
        e.target.value = this.firstInput;
        this.$emit('update:modelValue', this.firstInput);
        return true;
      }
      return false;
    },
  },
};
</script>

<style scoped>
.input-wrapper {
  position: relative;
  width: 100%;
  height: 40px;
  border-radius: 5px;
  border: var(--default--border);
  background: var(--White);
}

.cruise-input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: var(--Black);
  padding: 0 10px;
  border-radius: 5px;
}

.cruise-input:focus {
  border: 1px solid var(--Blue-blue-500, #446ceb);
}

.input-placeholder {
  display: inline-block;
  width: calc(100% - 20px);
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  font-size: 12px;
  color: #969696;
  user-select: none;
}

.required-star {
  font-size: 12px;
  color: var(--Orange-orange-500);
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox  */
input[type='number'] {
  -moz-appearance: textfield;
}

.error {
  border: 1px solid var(--Orange-orange-500, #f64852);
}

@media screen and (min-width: 768px) {
  .cruise-input {
    font-size: 14px;
  }

  .input-placeholder {
    font-size: 14px;
  }

  .required-star {
    font-size: 14px;
  }
}
</style>
